import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Home, ArrowLeft, UserPlus, Check, X, Trash2,
  Users, Clock, CheckCircle, XCircle, Shield, Pencil, Ban, ScrollText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import AppShell from '@/components/AppShell';
import EditUserModal from '@/components/EditUserModal';
import AuditLogList from '@/components/AuditLogList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsers() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'teacher', grade_level: '' });
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState('all');
  const [editingUser, setEditingUser] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers });
      setUsers(res.data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/admin/users`, {
        ...form,
        grade_level: form.grade_level ? parseInt(form.grade_level) : null
      }, { headers });
      toast.success('User created successfully');
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', role: 'teacher', grade_level: '' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const updateStatus = async (userId, status) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, { status }, { headers });
      toast.success(`User ${status}`);
      fetchUsers();
    } catch {
      toast.error('Failed to update user');
    }
  };

  const deleteUser = async (userId, name) => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const filtered = users.filter(u => {
    if (filter === 'pending') return u.status === 'pending';
    if (filter === 'approved') return u.status === 'approved';
    if (filter === 'rejected') return u.status === 'rejected';
    return true;
  });

  const pendingCount = users.filter(u => u.status === 'pending').length;

  const statusBadge = (status) => {
    if (status === 'approved') return <Badge className="bg-green-100 text-green-800 border-green-200 text-xs"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
    if (status === 'pending') return <Badge className="bg-[#F9F8F4] text-[#14213D] border-[#EBE7E0] text-xs"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    if (status === 'disabled') return <Badge className="bg-stone-200 text-stone-700 border-stone-300 text-xs"><Ban className="h-3 w-3 mr-1" />Disabled</Badge>;
    return <Badge className="bg-red-100 text-red-800 border-red-200 text-xs"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
  };

  const roleBadge = (role) => {
    if (role === 'admin') return <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-xs"><Shield className="h-3 w-3 mr-1" />Admin</Badge>;
    if (role === 'teacher') return <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-xs">Teacher</Badge>;
    return <Badge className="bg-[#F9F8F4] text-[#2D3748] border-[#EBE7E0] text-xs">Student</Badge>;
  };

  const tierBadge = (tier) => {
    const t = tier || 'free';
    if (t === 'pro') return <Badge className="bg-[#FFF8E7] text-[#8B6914] border-[#D4AF37] text-xs">Pro</Badge>;
    if (t === 'basic') return <Badge className="bg-[#EAF1FF] text-[#14213D] border-[#C5D3EB] text-xs">Basic</Badge>;
    return <Badge className="bg-[#F9F8F4] text-[#5A6577] border-[#EBE7E0] text-xs">Free</Badge>;
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Admin</p>
            <h1 className="font-serif-display text-3xl text-[#14213D] tracking-tight">User Management</h1>
            <p className="text-sm text-[#5A6577] mt-1">
              {users.length} total users{pendingCount > 0 && <span className="text-[#D4AF37] font-semibold"> &mdash; {pendingCount} pending approval</span>}
            </p>
          </div>
          <Button
            className="bg-[#14213D] hover:bg-[#0F1A2E] text-[#FDFBF7] text-sm"
            onClick={() => setShowCreate(!showCreate)}
            data-testid="create-user-btn"
          >
            <UserPlus className="h-4 w-4 mr-2" />{showCreate ? 'Cancel' : 'Create User'}
          </Button>
        </div>

        {/* Create User Form */}
        {showCreate && (
          <Card className="p-5 mb-6 border-[#EBE7E0] bg-white" data-testid="create-user-form">
            <h3 className="font-serif text-[#14213D] text-sm mb-4">Create New User</h3>
            <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#5A6577] mb-1">Name</label>
                <input
                  type="text" required value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})}
                  className="w-full px-3 py-2 border border-[#CBD5E0] rounded-md text-sm focus:ring-amber-500 focus:border-[#D4AF37]"
                  data-testid="create-user-name"
                />
              </div>
              <div>
                <label className="block text-xs text-[#5A6577] mb-1">Email</label>
                <input
                  type="email" required value={form.email}
                  onChange={e => setForm({...form, email: e.target.value})}
                  className="w-full px-3 py-2 border border-[#CBD5E0] rounded-md text-sm focus:ring-amber-500 focus:border-[#D4AF37]"
                  data-testid="create-user-email"
                />
              </div>
              <div>
                <label className="block text-xs text-[#5A6577] mb-1">Password</label>
                <input
                  type="password" required minLength={6} value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  className="w-full px-3 py-2 border border-[#CBD5E0] rounded-md text-sm focus:ring-amber-500 focus:border-[#D4AF37]"
                  data-testid="create-user-password"
                />
              </div>
              <div>
                <label className="block text-xs text-[#5A6577] mb-1">Role</label>
                <select
                  value={form.role}
                  onChange={e => setForm({...form, role: e.target.value})}
                  className="w-full px-3 py-2 border border-[#CBD5E0] rounded-md text-sm focus:ring-amber-500 focus:border-[#D4AF37]"
                  data-testid="create-user-role"
                >
                  <option value="teacher">Teacher</option>
                  <option value="student">Student</option>
                </select>
              </div>
              {form.role === 'student' && (
                <div>
                  <label className="block text-xs text-[#5A6577] mb-1">Grade Level</label>
                  <select
                    value={form.grade_level}
                    onChange={e => setForm({...form, grade_level: e.target.value})}
                    className="w-full px-3 py-2 border border-[#CBD5E0] rounded-md text-sm"
                  >
                    <option value="">Select grade</option>
                    {[7,8,9,10,11,12].map(g => <option key={g} value={g}>Grade {g}</option>)}
                  </select>
                </div>
              )}
              <div className="sm:col-span-2">
                <Button type="submit" disabled={creating} className="bg-[#14213D] hover:bg-[#2C1A14] text-[#FDFBF7] text-sm" data-testid="create-user-submit">
                  {creating ? 'Creating...' : 'Create User (Auto-Approved)'}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Filter tabs */}
        <Tabs defaultValue="users" className="space-y-4">
          <TabsList className="bg-[#F9F8F4]">
            <TabsTrigger value="users" data-testid="users-tab">
              <Users className="h-4 w-4 mr-2" />Users
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="audit-tab">
              <ScrollText className="h-4 w-4 mr-2" />Audit Log
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="space-y-4">
        <div className="flex gap-2 mb-4">
          {['all', 'pending', 'approved', 'rejected'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filter === f
                  ? 'bg-[#14213D] text-[#FDFBF7]'
                  : 'bg-[#F9F8F4] text-[#5A6577] hover:bg-[#EBE7E0]'
              }`}
              data-testid={`filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'pending' && pendingCount > 0 && (
                <span className="ml-1.5 bg-[#D4AF37] text-white rounded-full px-1.5 text-[11px]">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>

        {/* User list */}
        {loading ? (
          <div className="text-center py-12 text-[#5A6577] text-sm">Loading users...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-[#5A6577] text-sm">No users found</div>
        ) : (
          <div className="space-y-2">
            {filtered.map(u => (
              <Card key={u.id} className="p-4 border-0 shadow-sm bg-white hover:shadow-md transition-shadow" data-testid={`user-row-${u.id}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-[#14213D] truncate">{u.name}</span>
                      {roleBadge(u.role)}
                      {tierBadge(u.subscription_tier)}
                      {statusBadge(u.status || 'approved')}
                    </div>
                    <p className="text-xs text-[#5A6577] mt-0.5">{u.email}{u.grade_level ? ` — Grade ${u.grade_level}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {(u.status === 'pending' || u.status === 'rejected') && (
                      <Button size="sm" variant="outline" className="h-8 border-green-300 text-green-700 hover:bg-green-50 text-xs"
                        onClick={() => updateStatus(u.id, 'approved')} data-testid={`approve-${u.id}`}>
                        <Check className="h-3.5 w-3.5 mr-1" />Approve
                      </Button>
                    )}
                    {(u.status === 'pending' || u.status === 'approved') && u.role !== 'admin' && (
                      <Button size="sm" variant="outline" className="h-8 border-red-300 text-red-700 hover:bg-red-50 text-xs"
                        onClick={() => updateStatus(u.id, 'rejected')} data-testid={`reject-${u.id}`}>
                        <X className="h-3.5 w-3.5 mr-1" />Reject
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="h-8 border-[#CBD5E0] text-[#14213D] hover:bg-[#F9F8F4] text-xs"
                      onClick={() => setEditingUser(u)} data-testid={`edit-${u.id}`}>
                      <Pencil className="h-3.5 w-3.5 mr-1" />Edit
                    </Button>
                    {u.role !== 'admin' && (
                      <Button size="sm" variant="ghost" className="h-8 text-[#A0AEC0] hover:text-red-600 text-xs"
                        onClick={() => deleteUser(u.id, u.name)} data-testid={`delete-${u.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
          </TabsContent>

          <TabsContent value="audit">
            <AuditLogList token={token} />
          </TabsContent>
        </Tabs>
      </div>
      {editingUser && (
        <EditUserModal
          user={editingUser}
          token={token}
          onClose={() => setEditingUser(null)}
          onSaved={(updated) => {
            setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
          }}
        />
      )}
    </AppShell>
  );
}
